"use client";

import { NextIntlClientProvider } from "next-intl";
import { useEffect, useState } from "react";
import {
  QueryClient,
  QueryClientProvider,
  type QueryClientConfig,
} from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";

const queryClientConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      retry: 2,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
};

export default function ClientLayout({
  children,
  locale,
  messages,
}: {
  children: React.ReactNode;
  locale: string;
  messages: any;
}) {
  const [queryClient] = useState(
    () => new QueryClient(queryClientConfig)
  );
  const [persister, setPersister] =
    useState<ReturnType<typeof createSyncStoragePersister>>();

  useEffect(() => {
    if (!localStorage.getItem("language")) {
      localStorage.setItem("language", "en");
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    setPersister(
      createSyncStoragePersister({
        storage: window.sessionStorage,
        key: "gritizer-query-cache",
        throttleTime: 1000,
      })
    );
  }, []);

  if (persister) {
    return (
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister,
          dehydrateOptions: {
            shouldDehydrateQuery: (query) =>
              query.state.status === "success",
          },
        }}
      >
        <NextIntlClientProvider
          locale={locale}
          messages={messages}
          timeZone="UTC" // ✅ Add the default timezone here
        >
          {children}
        </NextIntlClientProvider>
      </PersistQueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <NextIntlClientProvider
        locale={locale}
        messages={messages}
        timeZone="UTC" // ✅ Add the default timezone here
      >
        {children}
      </NextIntlClientProvider>
    </QueryClientProvider>
  );
}
